import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { usePlan, usePlanProgress, useLogProgress, useDeleteProgress } from '../hooks/usePlans';
import { useAuthStore } from '../store/authStore';
import { useT } from '../hooks/useT';
import Input from '../components/common/Input';
import Button from '../components/common/Button';

function fmt(value, currency = 'BRL') {
  const locale = currency === 'BRL' ? 'pt-BR' : currency === 'EUR' ? 'de-DE' : 'en-US';
  return parseFloat(value || 0).toLocaleString(locale, {
    style: 'currency',
    currency,
    maximumFractionDigits: 0,
  });
}

function formatDate(dateStr) {
  if (!dateStr) return '—';
  return new Date(String(dateStr).slice(0, 10) + 'T12:00:00').toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

export default function PlanProgressPage() {
  const { id } = useParams();
  const { user } = useAuthStore();
  const t = useT();
  const currency = user?.currency || 'BRL';

  const { data: plan, isLoading: planLoading } = usePlan(id);
  const { data: progressData, isLoading: progressLoading } = usePlanProgress(id);
  const { mutateAsync: logProgress, isPending, error: logError, isSuccess } = useLogProgress(id);
  const { mutateAsync: deleteProgress, isPending: isDeleting } = useDeleteProgress(id);
  const [deletingId, setDeletingId] = useState(null);

  async function handleDelete(entry) {
    if (!window.confirm(t('progress.delete.confirm').replace('{notes}', entry.notes).replace('{amount}', fmt(entry.amount_saved, currency)))) return;
    setDeletingId(entry.id);
    try {
      await deleteProgress(entry.id);
    } finally {
      setDeletingId(null);
    }
  }

  const [amount, setAmount] = useState('');
  const [notes, setNotes] = useState('');
  const [formError, setFormError] = useState('');

  async function handleSubmit(e) {
    e.preventDefault();
    setFormError('');
    const parsed = parseFloat(amount.replace(',', '.'));
    if (isNaN(parsed) || parsed <= 0) {
      setFormError(t('progress.form.error.amount'));
      return;
    }
    if (!notes.trim()) {
      setFormError(t('progress.form.error.notes'));
      return;
    }
    await logProgress({ amount_saved: parsed, notes: notes.trim() });
    setAmount('');
    setNotes('');
  }

  if (planLoading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="h-6 bg-surface-card rounded w-48" />
        <div className="h-28 bg-surface-card rounded-2xl" />
        <div className="h-64 bg-surface-card rounded-2xl" />
      </div>
    );
  }

  if (!plan) {
    return (
      <div className="card text-center py-12">
        <p className="text-red-400">{t('progress.not_found')} <Link to="/goals" className="underline">{t('progress.breadcrumb.goals')}</Link></p>
      </div>
    );
  }

  const entries = progressData?.entries || [];
  const totalSaved = progressData?.total_saved ?? 0;
  const pct = progressData?.percent_complete ?? 0;
  const goalLabel = t(`goal.type.${plan.goal_type}`) || plan.goal_type;

  return (
    <div className="space-y-8 animate-fade-in max-w-3xl mx-auto">

      {/* Breadcrumb */}
      <p className="text-sm text-slate-500">
        <Link to="/goals" className="hover:text-slate-300 transition-colors">{t('progress.breadcrumb.goals')}</Link>
        {' / '}
        <Link to={`/plans/${plan.id}`} className="hover:text-slate-300 transition-colors">{plan.goal_title}</Link>
        {' / '}
        <span className="text-slate-400">{t('progress.breadcrumb')}</span>
      </p>

      {/* Goal info card */}
      <div className="card space-y-4">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-xs text-slate-500 mb-1">{goalLabel}</p>
            <h1 className="text-xl font-bold text-slate-100">{plan.goal_title}</h1>
          </div>
          <Link to={`/plans/${plan.id}`} className="text-xs text-brand-400 hover:text-brand-300 transition-colors shrink-0">
            {t('progress.back')}
          </Link>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-1">
          <div>
            <p className="text-xs text-slate-500 mb-0.5">{t('progress.stat.cost')}</p>
            <p className="font-semibold text-slate-200 text-sm">{fmt(plan.effective_cost, currency)}</p>
          </div>
          <div>
            <p className="text-xs text-slate-500 mb-0.5">{t('progress.stat.monthly')}</p>
            <p className="font-semibold text-slate-200 text-sm">{fmt(plan.monthly_savings_target, currency)}</p>
          </div>
          <div>
            <p className="text-xs text-slate-500 mb-0.5">{t('progress.stat.deadline')}</p>
            <p className="font-semibold text-slate-200 text-sm">{formatDate(plan.goal_deadline)}</p>
          </div>
          <div>
            <p className="text-xs text-slate-500 mb-0.5">{t('progress.stat.timeline')}</p>
            <p className="font-semibold text-slate-200 text-sm">{plan.estimated_months ? t('progress.stat.timeline.value').replace('{n}', plan.estimated_months) : '—'}</p>
          </div>
        </div>

        {/* Progress bar */}
        <div className="space-y-1 pt-1">
          <div className="flex justify-between text-xs text-slate-500">
            <span>
              {t('progress.bar.saved')} <span className="text-slate-300 font-medium">{fmt(totalSaved, currency)}</span>
              {' '}{t('progress.bar.of')}{' '}
              <span className="text-slate-300 font-medium">{fmt(plan.effective_cost, currency)}</span>
            </span>
            <span className="text-brand-400 font-semibold">{pct.toFixed(1)}%</span>
          </div>
          <div className="h-3 bg-surface rounded-full overflow-hidden">
            <div
              className="h-full bg-brand-500 rounded-full transition-all duration-500"
              style={{ width: `${Math.min(100, pct)}%` }}
            />
          </div>
        </div>
      </div>

      {/* Two-column: form + history */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">

        {/* Log Progress form */}
        <div className="card">
          <h2 className="font-semibold text-slate-200 mb-4">{t('progress.form.title')}</h2>
          <form onSubmit={handleSubmit} className="space-y-3">
            <Input
              label={t('progress.form.amount')}
              type="number"
              min="0"
              step="0.01"
              placeholder="500.00"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              required
            />
            <Input
              label={t('progress.form.description')}
              placeholder={t('progress.form.description_placeholder')}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              required
            />
            {(formError || logError) && (
              <p className="text-red-400 text-sm">{formError || logError?.message}</p>
            )}
            {isSuccess && !formError && (
              <p className="text-green-400 text-sm">{t('progress.form.success')}</p>
            )}
            <Button type="submit" isLoading={isPending} className="w-full">
              {t('progress.form.submit')}
            </Button>
          </form>
        </div>

        {/* Progress History */}
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold text-slate-200">{t('progress.history.title')}</h2>
            {!progressLoading && (
              <span className="text-xs text-slate-500">
                {entries.length} {entries.length === 1 ? t('common.entry') : t('common.entries')}
              </span>
            )}
          </div>

          {progressLoading ? (
            <div className="space-y-3 animate-pulse">
              {[1, 2, 3].map((i) => <div key={i} className="h-14 bg-surface rounded-xl" />)}
            </div>
          ) : entries.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-3xl mb-2">📋</p>
              <p className="text-slate-400 text-sm">{t('progress.history.empty.title')}</p>
              <p className="text-slate-500 text-xs mt-1">{t('progress.history.empty.sub')}</p>
            </div>
          ) : (
            <div className="space-y-2 max-h-[480px] overflow-y-auto pr-1">
              {[...entries].reverse().map((entry) => (
                <div
                  key={entry.id}
                  className="flex items-start gap-3 rounded-xl border border-surface bg-surface/30 px-4 py-3 group"
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-slate-200 text-sm leading-snug">{entry.notes}</p>
                    <p className="text-slate-500 text-xs mt-1">{formatDate(entry.entry_date)}</p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-green-400 font-semibold text-sm">{fmt(entry.amount_saved, currency)}</p>
                    <p className="text-slate-500 text-xs mt-0.5">{fmt(entry.cumulative_savings, currency)} total</p>
                  </div>
                  <button
                    onClick={() => handleDelete(entry)}
                    disabled={isDeleting && deletingId === entry.id}
                    className="shrink-0 opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity text-slate-600 hover:text-red-400 disabled:opacity-30 ml-1 mt-0.5"
                    title={t('progress.history.remove')}
                  >
                    {isDeleting && deletingId === entry.id ? '…' : '✕'}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

      </div>
    </div>
  );
}
